import { TextField, TextFieldProps, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FormikProps } from 'formik';
import { get } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce/lib';

const useStyles = makeStyles((theme: Theme) => ({
  readonly: {
    backgroundColor: theme.palette.mode === 'dark' ? '#3a3a3a' : '#f6f6f6',
  },
}));

interface IFormikTextField {
  /** Name of formik field */
  name: string;
  /** Formik object */
  formik: FormikProps<any>;
  /** Field helper text */
  helperText?: string;
  /** Set time between field update and formik change (default: 350) [set to 0 to turn off] */
  debounceTimeout?: number;
}

/**
 * Wrapper around Material-UI TextField that supports Formik and
 * updates field using debouncing changes for better performance.
 *
 * @implements TextField
 * @param param Parameters accepted by TextField
 */
export const FormikTextField = ({
  name,
  formik,
  helperText,
  debounceTimeout = 350,
  InputProps,
  dataPup,
  ...rest
}: IFormikTextField & TextFieldProps & { dataPup?: string }) => {
  const classes = useStyles();

  // cache if formik/name changes
  const formikProps = name
    ? formik?.getFieldProps(name)
    : {
        name: '',
        value: '',
        onChange: () => {},
      };

  const [eventState, setEventState] = useState({
    target: {
      name: formikProps.name,
      value: formikProps.value,
    },
  });

  // sync formik with local state
  const { callback: debouncedOnChange } = useDebouncedCallback(
    formikProps.onChange,
    debounceTimeout
  );

  const handleChange = useCallback(
    (event: any) => setEventState({ ...event }),
    [setEventState]
  );

  // sync local state with formik (for reinitialize=true)
  // isolate change only to formik name/value
  useEffect(() => {
    if (
      formikProps.name !== eventState.target.name ||
      formikProps.value !== eventState.target.value
    ) {
      setEventState({
        target: {
          name: formikProps.name,
          value: formikProps.value,
        },
      });
    }
  }, [formikProps.name, formikProps.value]);

  // sync values with formik
  useEffect(() => {
    debouncedOnChange(eventState);
  }, [eventState]);

  const touched = get(formik.touched, name);
  const errors = get(formik.errors, name);

  return (
    <TextField
      InputProps={
        {
          ...InputProps,
          'data-pup': dataPup ?? undefined,
          className: clsx(InputProps?.className, {
            [classes.readonly]: InputProps?.readOnly,
          }),
        } as any
      }
      inputProps={{
        'data-pup': dataPup ?? undefined,
      }}
      {...rest}
      {...formikProps}
      value={
        debounceTimeout && name ? eventState.target.value : formikProps.value
      } // if debounced off use formik
      onChange={debounceTimeout && name ? handleChange : formikProps.onChange} // if debounced off use formik
      error={formik && !!touched && !!errors}
      helperText={(formik && touched && errors) || helperText}
    />
  );
};
